import { lazy, Suspense, useEffect, useState } from 'react';
import { FullLazyMotion } from '~ui/components';
import { TrialProvider } from '~features/trial';
import { useTracking } from '~features/tracking';
import type { CompanyProfileJobAdProfileFragment } from '~api';
import { Wrapper } from './styled-components';

const CompanyInfo = lazy(() => import('./company-info/company-info'));
const Teaser = lazy(() => import('./teaser/teaser'));

type Props = {
  profile: CompanyProfileJobAdProfileFragment | null | undefined;
  className?: string;
  asTeaser?: boolean;
};

export const JobAdSection = ({ profile, className, asTeaser }: Props) => {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const { trackPageView } = useTracking();
  const { publishedAt } = profile ?? {};

  useEffect(() => {
    if (!ref) {
      return;
    }

    let timeout: NodeJS.Timeout;
    const intersectionObserver = new IntersectionObserver(
      ([{ intersectionRatio }], observer) => {
        if (intersectionRatio >= 0.75) {
          timeout = setTimeout(() => {
            trackPageView('company_info_view', {
              touchPointButton: asTeaser ? 'emp_teaser' : 'company_info',
            });
            observer.disconnect();
          }, 1000);
        } else {
          clearTimeout(timeout);
        }
      },
      { threshold: [0.75] },
    );

    intersectionObserver.observe(ref);

    return () => {
      clearTimeout(timeout);
      intersectionObserver.disconnect();
    };
  }, [ref, asTeaser, trackPageView]);

  if (!publishedAt) {
    return asTeaser ? (
      <Wrapper ref={setRef} className={className}>
        <Suspense fallback={null}>
          <FullLazyMotion>
            <TrialProvider>
              <Teaser />
            </TrialProvider>
          </FullLazyMotion>
        </Suspense>
      </Wrapper>
    ) : null;
  }

  return (
    <Wrapper ref={setRef} className={className}>
      <Suspense fallback={null}>
        <CompanyInfo profile={profile} />
      </Suspense>
    </Wrapper>
  );
};
